import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { saveFiltersToStorage, loadFiltersFromStorage } from '../utils/filterStorage';
import DateInput from './DateInput';

interface ProfitLossItem {
  category: number;
  categoryTitle: string;
  amount: number;
}

interface TableState {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filterText: string;
  currentPage: number;
  pageSize: number;
}

interface ProfitLossReport {
  incomeItems: ProfitLossItem[];
  costItems: ProfitLossItem[];
  operationalItems: ProfitLossItem[];
  otherItems: ProfitLossItem[];
  totalIncome: number;
  totalCosts: number;
  grossProfit: number;
  totalOperational: number;
  operationalProfit: number;
  totalOther: number;
  netProfit: number;
  ebitda: number;
}

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const ProfitLoss: React.FC = () => {
  const [reportData, setReportData] = useState<ProfitLossReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Load initial filters from localStorage
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(0);
    date.setDate(1);
    return formatDateForInput(date);
  };

  const defaultFilters = {
    startDate: getDefaultStartDate(),
    endDate: formatDateForInput(new Date())
  };
  const initialFilters = loadFiltersFromStorage('profit-loss', defaultFilters);

  const [startDate, setStartDate] = useState<string>(initialFilters.startDate);
  const [endDate, setEndDate] = useState<string>(initialFilters.endDate);

  // Table states for each section
  const [incomeTableState, setIncomeTableState] = useState<TableState>({
    sortBy: 'categoryTitle',
    sortOrder: 'asc',
    filterText: '',
    currentPage: 1,
    pageSize: 10
  });
  const [costTableState, setCostTableState] = useState<TableState>({
    sortBy: 'categoryTitle',
    sortOrder: 'asc',
    filterText: '',
    currentPage: 1,
    pageSize: 10
  });
  const [operationalTableState, setOperationalTableState] = useState<TableState>({
    sortBy: 'categoryTitle',
    sortOrder: 'asc',
    filterText: '',
    currentPage: 1,
    pageSize: 10
  });
  const [otherTableState, setOtherTableState] = useState<TableState>({
    sortBy: 'categoryTitle',
    sortOrder: 'asc',
    filterText: '',
    currentPage: 1,
    pageSize: 10
  });

  const generateReport = useCallback(async (startDateOverride?: string, endDateOverride?: string) => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const startDateToUse = startDateOverride || startDate;
      const endDateToUse = endDateOverride || endDate;
      
      // Save filter to localStorage
      saveFiltersToStorage('profit-loss', { startDate: startDateToUse, endDate: endDateToUse });
      
      const response = await api.get('/profitloss', {
        params: {
          startDate: startDateToUse,
          endDate: endDateToUse,
        }
      });

      setReportData(response.data);
    } catch (error) {
      console.error('Error generating profit & loss report:', error);
      setErrorMessage('Unable to load the profit and loss report. Please adjust the filters and try again.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Initial load with current dates
    generateReport(startDate, endDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Enhanced table rendering with filtering, sorting, and pagination
  const renderDataTable = (title: string, items: ProfitLossItem[], tableState: TableState, setTableState: (state: TableState) => void) => {
    // Filter items based on search text
    const filteredItems = items.filter(item => 
      item.categoryTitle.toLowerCase().includes(tableState.filterText.toLowerCase())
    );

    // Sort items
    const sortedItems = [...filteredItems].sort((a, b) => {
      const aValue = tableState.sortBy === 'categoryTitle' ? a.categoryTitle : a.amount;
      const bValue = tableState.sortBy === 'categoryTitle' ? b.categoryTitle : b.amount;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return tableState.sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return tableState.sortOrder === 'asc' 
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    // Calculate pagination
    const totalPages = Math.ceil(sortedItems.length / tableState.pageSize);
    const startIndex = (tableState.currentPage - 1) * tableState.pageSize;
    const paginatedItems = sortedItems.slice(startIndex, startIndex + tableState.pageSize);

    const handleSort = (column: string) => {
      setTableState({
        ...tableState,
        sortBy: column,
        sortOrder: tableState.sortBy === column && tableState.sortOrder === 'asc' ? 'desc' : 'asc',
        currentPage: 1
      });
    };

    const handleFilter = (filterText: string) => {
      setTableState({
        ...tableState,
        filterText,
        currentPage: 1
      });
    };

    const handlePageChange = (page: number) => {
      setTableState({
        ...tableState,
        currentPage: page
      });
    };

    const getSortIcon = (column: string) => {
      if (tableState.sortBy !== column) {
        return <i className="fa-solid fa-sort" aria-hidden="true" />;
      }

      return tableState.sortOrder === 'asc'
        ? <i className="fa-solid fa-sort-up" aria-hidden="true" />
        : <i className="fa-solid fa-sort-down" aria-hidden="true" />;
    };

    return (
    <div style={{ marginBottom: '2rem' }}>
      <h4 style={{ 
        color: 'var(--text-primary)',
        marginBottom: '1rem',
        fontSize: 'var(--font-size-lg)',
        fontWeight: '600'
      }}>
        {title}
      </h4>
        
        {/* Filter input */}
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Filter categories..."
            value={tableState.filterText}
            onChange={(e) => handleFilter(e.target.value)}
            style={{ maxWidth: '300px' }}
          />
        </div>

      <div>
        <table className="table table-hover">
          <thead>
            <tr>
                <th 
                  style={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    textAlign: 'left',
                    width: '70%'
                  }}
                  onClick={() => handleSort('categoryTitle')}
                >
                  Category {getSortIcon('categoryTitle')}
              </th>
                <th 
                  style={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    textAlign: 'left',
                    width: '30%'
                  }}
                  onClick={() => handleSort('amount')}
                >
                  Amount {getSortIcon('amount')}
              </th>
            </tr>
          </thead>
          <tbody>
              {paginatedItems.map((item, index) => (
              <tr key={index}>
                <td style={{ color: 'var(--text-primary)', width: '70%' }}>
                  {item.categoryTitle}
                </td>
                <td style={{ 
                  color: item.amount >= 0 ? 'var(--success)' : 'var(--danger)',
                  fontWeight: '500',
                  width: '30%'
                }}>
                  ${Math.abs(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small style={{ color: 'var(--text-muted)' }}>
              Showing {startIndex + 1} to {Math.min(startIndex + tableState.pageSize, sortedItems.length)} of {sortedItems.length} entries
            </small>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${tableState.currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(tableState.currentPage - 1)}
                    disabled={tableState.currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i + 1} className={`page-item ${tableState.currentPage === i + 1 ? 'active' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${tableState.currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(tableState.currentPage + 1)}
                    disabled={tableState.currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
    </div>
  );
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          color: 'var(--text-primary)', 
          fontSize: 'var(--font-size-3xl)', 
          fontWeight: '700',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          P&L Report
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
          Comprehensive profit and loss analysis
        </p>
      </div>
      
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-3">
            <h5 className="card-title mb-0">Financial Report</h5>
            <div className="ms-auto d-flex flex-wrap gap-2 align-items-end" style={{ justifyContent: 'flex-end' }}>
              <div>
                <DateInput
                  className="form-control"
                  value={startDate}
                  maxDate={endDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    saveFiltersToStorage('profit-loss', { startDate: newValue, endDate });
                  }}
                />
              </div>

              <div>
                <DateInput
                  className="form-control"
                  value={endDate}
                  minDate={startDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                    saveFiltersToStorage('profit-loss', { startDate, endDate: newValue });
                  }}
                />
              </div>

              <div>
                <button
                  className="btn btn-primary btn-sm d-flex align-items-center justify-content-center"
                  onClick={() => generateReport(startDate, endDate)}
                  disabled={loading}
                  style={{ minWidth: '140px', height: '40px', marginBottom: '0.4rem' }}
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}

          {loading && !reportData ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div className="spinner-border" role="status"></div>
              <p style={{ color: 'var(--text-muted)' }}>Generating report...</p>
            </div>
          ) : reportData ? (
            <>
              {renderDataTable('Income', reportData.incomeItems, incomeTableState, setIncomeTableState)}
              {renderDataTable('Cost of Services', reportData.costItems, costTableState, setCostTableState)}
              {renderDataTable('Operational Expenses', reportData.operationalItems, operationalTableState, setOperationalTableState)}
              {renderDataTable('Other Income and Expenses', reportData.otherItems, otherTableState, setOtherTableState)}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '1.5rem',
                marginTop: '2rem'
              }}>
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">Financial Summary</h5>
                  </div>
                  <div className="card-body">
                    {[
                      { label: 'Total Income', value: reportData.totalIncome, color: 'var(--success)' },
                      { label: 'Cost of Services', value: reportData.totalCosts, color: 'var(--danger)' },
                      { label: 'Gross Profit', value: reportData.grossProfit, color: 'var(--primary)' },
                      { label: 'Operational Expenses', value: reportData.totalOperational, color: 'var(--danger)' },
                      { label: 'Operational Profit', value: reportData.operationalProfit, color: 'var(--primary)' },
                      { label: 'Other Income and Expenses', value: reportData.totalOther, color: 'var(--danger)' },
                      { label: 'Net Profit', value: reportData.netProfit, color: 'var(--primary)', bold: true },
                      { label: 'EBITDA', value: reportData.ebitda, color: 'var(--primary)' }
                    ].map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem 0',
                          borderBottom: index < 7 ? '1px solid var(--border-color)' : 'none'
                        }}
                      >
                        <span style={{
                          color: 'var(--text-primary)',
                          fontWeight: item.bold ? '700' : '500'
                        }}>
                          {item.label}:
                        </span>
                        <span style={{
                          color: item.color,
                          fontWeight: item.bold ? '700' : '500',
                          fontSize: item.bold ? 'var(--font-size-lg)' : 'var(--font-size-base)'
                        }}>
                          ${item.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: 'var(--text-muted)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                <i className="fa-solid fa-chart-pie" aria-hidden="true"></i>
              </div>
              <h5>No data available</h5>
              <p>No data available for the selected date range.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfitLoss;
